import React, { Component } from "react";
import { Row, Col, Label, FormGroup } from "reactstrap";
import { AppBadge, AppCard, PageLayout, StatusBadge, createTabHeaderTemplate, LoadingDots } from "../../components/ui";

import { IndividualStatus, UpdateUserPhotoRequest, User, UserTypeRelation } from "../../models/User";
import { classNames } from "primereact/utils";
import { Download, Loader2 } from "lucide-react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";

import GeneralDataComponent from "./GeneralData";
import UserKycSubmissionsComponent from "./UserKycSubmissionsComponent";
import AdditionalCustomerDocumentsPanel from "./AdditionalCustomerDocumentsPanel";
import UserDocumentsComponent from "./UserDocumentsComponent";
import FinancialDataComponent from "./FinancialData";
import { featureFlagApiService } from "../../services/api/featureFlags/FeatureFlagApiService";

const MERCHANT_PORTAL_DOCUMENTS_FLAG = "merchantportal.additional_documents";
import UserTransactions from "./UserTransactions";
import UserJournalingComponent from "./UserJournalingComponent";
import UserDevicesComponent from "./UserDevicesComponent";
import UserCommentsComponent from "./UserCommentsComponent";
import NilWhitelistComponent from "../Kyc/NilWhitelistComponent";
import InlineEditableInput, { InputType } from "../../components/InlineEditableInput";
import { toastService } from "../../services/toastService";
import { userApiService } from "../../services/api/users/UsersApiService";
import { SelectItem } from "../../models/SelectItem";
import { utils } from "../../utils/utils";
import { ProfileImage } from "../../components/Image/ProfileImage";
import { fileService } from "../../services/FileService";
import { merchantGroupsApiService } from "../../services/api/merchants/MerchantGroupsApiService";
import { MerchantGroup } from "../../models/MerchantModel";
import { kycSubmissionApiService } from "../../services/api/Kyc/KycSubmissionApiService";
import { orchestratorApiService } from "../../services/api/Compliance/OrchestratorApiService";

import history from "../../history";
import { TabPanel, TabView } from "primereact/tabview";
import _ from "lodash";
import { isComplianceOfficer, isComplianceOfficerApprover, isDataManager, canSoftDeleteUser, isIndividualTransactionViewAllowed, isIndividualDocumentsViewAllowed, isJournalingAllowed, isKYCApproveAllowed, isSuperAdmin, isUserProfileUpdateAllowed } from "../../authGuard";
import MerchantWalletsComponent, { WalletType } from "../Merchants/Wallets";
import { storageService } from "../../services/StorageService";
import { confirmAction } from "../../utils/confirmHelpers";
import { UserCog } from "lucide-react";
import JSZip from "jszip";
import saveAs from "file-saver";
import { Button } from "primereact/button";
import { Image_File_Size } from "../../models/AppConstants";
import imageCompression from "browser-image-compression";
import { KycSubmissionDocument } from "../../models/KycSubmission";
import { authUsersApiService } from "../../services/api/clients/AuthUsersApiService";
import DeleteUserDialog from "./DeleteUserDialog";
import { CustomerOutgoingFreezeRequest, CustomerOutgoingFreezeState, EntityType } from "../../models/Orchestrator";

type DeleteAction = "SOFT_DELETE" | "HARD_DELETE" | "RESTORE";
interface UserProfileState {
    userId: number;
    activeTabIndex: number;
    user: User | null;
    isLoading: boolean;
    groupsSelectItem: SelectItem<number>[];
    isDocumentLoading: boolean;
    photoUploading: boolean;
    isDeleteConfirmModalVisible: boolean,
    deleteAction: DeleteAction | null,
    outgoingFreeze: CustomerOutgoingFreezeState | null;
    outgoingFreezeLoading: boolean;
    outgoingFreezeActionLoading: boolean;
    outgoingFreezeReason: string;
    documentsFeatureEnabled: boolean;
}

export default class UserProfileComponent extends Component<Record<string, never>, UserProfileState> {
    tabNames: string[] = ["General", "KYC", "Financial", "Misc", "Journaling", "Transactions"];

    userStatusClassesMap: Map<string, string> = new Map<string, string>([
        ["Active", "success"],
        ["Deactivated", "danger"],
        ["Closed", "danger"],
        ["New", "success"],
        ["Pending Verification", "warning"],
        ["Blocked", "danger"],
    ]);
    reject = () => {
        toastService.warn("You have rejected", "Rejected");
    };
    resetEmailDialog = () => {
        confirmAction(
            "Are you sure to reset email address?",
            "Confirmation",
            () => {
                this.resetEmail(this.state.userId);
            },
        );
    };
    resetMobileDialog = () => {
        confirmAction(
            "Are you sure to reset mobile?",
            "Confirmation",
            () => {
                this.resetMobile(this.state.userId);
            },
        );
    };

    resetKycStatusDialog = () => {
        confirmAction(
            "Are you sure to reset KYC Status?",
            "Confirmation",
            () => {
                this.resetKycStatus(this.state.userId);
            },
        );
    };
    deactivateDialog = () => {
        confirmAction(
            "Are you sure to deactivate user status?",
            "Confirmation",
            () => {
                this.deactiveOrActiveUserAccount(this.state.userId);
            },
        );
    };
    userKycStatusClassesMap: Map<string, string> = new Map<string, string>([
        ["Pending Review", "warning"],
        ["Under Review", "warning"],
        ["Approved", "success"],
        ["Rejected", "danger"],
        ["New", "primary"],
        ["Closed", "neutral"],
    ]);

    cbsOnboardedClassesMap: Map<boolean, string> = new Map<boolean, string>([
        [true, "success"],
        [false, "danger"],
    ]);

    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            userId: utils.getIdentifierFromPath(),
            activeTabIndex: 0,
            user: null,
            isLoading: false,
            groupsSelectItem: [],
            isDocumentLoading: false,
            photoUploading: false,
            isDeleteConfirmModalVisible: false,
            deleteAction: null,
            outgoingFreeze: null,
            outgoingFreezeLoading: false,
            outgoingFreezeActionLoading: false,
            outgoingFreezeReason: "",
            documentsFeatureEnabled: false,

        };
    }

    statusTypes: Map<number, string> = new Map<number, string>();
    kycStatusTypes: Map<number, string> = new Map<number, string>();
    kycLevels: SelectItem<number>[] = [];

    async componentDidMount() {
        this.setState({ isLoading: true });

        featureFlagApiService.isEnabled(MERCHANT_PORTAL_DOCUMENTS_FLAG).then((enabled) => {
            this.setState({ documentsFeatureEnabled: enabled });
        });

        const userProfile = await userApiService.get(this.state.userId);

        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        storageService.setSelectedMerchantId(userProfile.data.merchantId.toString());

        this.setState({ isLoading: false });

        const statusTypes = await userApiService.getStatusTypes();

        if (!statusTypes.success) {
            this.setState({ isLoading: false });
            toastService.error(statusTypes.message);
            return;
        }

        statusTypes.data.forEach((item) => {
            this.statusTypes.set(item.value, item.label);
        });

        const KycStatusTypes = await kycSubmissionApiService.getKycStatusTypes();

        if (!KycStatusTypes.success) {
            this.setState({ isLoading: false });
            toastService.error(KycStatusTypes.message);
            return;
        }

        KycStatusTypes.data.forEach((item) => {
            this.kycStatusTypes.set(item.value, item.label);
        });

        const kycLevels = await kycSubmissionApiService.getKycLevels();

        if (!kycLevels.success) {
            this.setState({ isLoading: false });
            toastService.error(kycLevels.message);
            return;
        }

        this.kycLevels = kycLevels.data;

        this.setState({ isLoading: false });

        const groupResult = await merchantGroupsApiService.getAll(userProfile.data.merchantId);

        if (!groupResult.success) {
            this.setState({ isLoading: false });
            toastService.error(groupResult.message);
            return;
        }

        this.setState({ user: userProfile.data, groupsSelectItem: this.getSelectMerchantGroupItems(groupResult.data) }, async () => {
            await this.loadImage();
            await this.loadOutgoingFreezeStatus();
        });
    }

    loadImage = async () => {
        if (this.state.user?.photo == null) {
            return;
        }
        const photoResult = await fileService.GetFile(this.state.user?.photo);

        if (!photoResult.success) {
            toastService.error("Could not load User Photo. Please contact your administrator");
            return;
        }

        const user = this.state.user;
        user.photo = photoResult.data;
        this.setState({ user: user });
    };

    getSelectMerchantGroupItems = (options: MerchantGroup[]): SelectItem<number>[] => {
        return options.map(
            (data) =>
                ({
                    label: data.name,
                    value: data.id,
                }) as SelectItem<number>,
        );
    };

    getVerificationClassName = (param: boolean): string => {
        return param === true ? "success" : "danger";
    };

    getCryptoCustomerClassName = (param: boolean): string => {
        return param === true ? "text-primary" : "text-danger";
    };

    getStatusVerificationClassName = (param: number): string => {
        const item = this.statusTypes.get(param);
        return (item != null ? this.userStatusClassesMap.get(item) : undefined) ?? "default";
    };

    getKycStatusVerificationClassName = (param: number): string => {
        const item = this.kycStatusTypes.get(param);
        return (item != null ? this.userKycStatusClassesMap.get(item) : undefined) ?? "default";
    };

    getCbsOnboardedClassName = (param: boolean): string => {
        return param != null ? this.cbsOnboardedClassesMap.get(param)! : "danger";
    };

    getKycLevel = (param: number): string => {
        const list = _.filter(this.kycLevels, (c) => c.value == param);
        return list.length > 0 ? list[0].label : "N/A";
    };
    updateValue = (fieldName: string, value: string) => {
        const user = this.state.user as any;

        user[fieldName] = value;

        this.setState({ user: user });
    };

    handleCryptoStatusChange(e: CheckboxChangeEvent) {
        const checked = (e as any).checked;
        const user = this.state.user as User;
        user.cryptoCustomer = checked;

        this.setState({ user: user });
    }

    onKycLevelSubmit = async (kycLevelId: number) => {
        this.setState({ isLoading: true });

        const objToPost: User = {
            id: this.state.userId,
            kycLevelId: kycLevelId,
        } as User;

        const KycLevel = await userApiService.updatesKycLevel(objToPost);
        const user = this.state.user as User;

        if (!KycLevel.success) {
            this.setState({ isLoading: false });
            const kycStatus = this.kycStatusTypes.get(this.state.user?.kycStatus ?? 1);
            const message = kycStatus === "New" ? KycLevel.message : `Kyc Level can not be changed at ${kycStatus} status.`;
            toastService.error(message);
            return;
        }

        user.kycLevel = kycLevelId;
        this.setState({ user: user, isLoading: false });
    };

    showToast = (message: string) => {
        toastService.success(message, "Action Completed");
    };

    deactiveOrActiveUserAccount = async (id: number) => {
        if (this.state.user?.status && this.statusTypes.get(this.state.user.status) === "Active") {
            this.deactivateUser(id);
            return;
        }

        this.activateUser(id);
    };

    deactivateUser = async (id: number) => {
        this.setState({ isLoading: true });
        const result = await userApiService.deactivateUser(id);

        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }

        // TODO. Fix it.

        const userProfile = await userApiService.get(this.state.userId);

        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        this.setState({ user: userProfile.data, isLoading: false });

        toastService.success("Account Deactivated.", "Action Completed");
    };

    closeUser = async (id: number) => {
        this.setState({ isLoading: true });
        const result = await userApiService.delete(id);

        if (!result.success) {
            this.setState({ isLoading: false });
            toastService.error(result.message);
            return;
        }

        // TODO. Fix it.

        const userProfile = await userApiService.get(this.state.userId);

        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        this.setState({ user: userProfile.data, isLoading: false });

        toastService.success("Account Closed.", "Action Completed");
    };

    activateUser = async (id: number) => {
        this.setState({ isLoading: true });
        const activeUser = await userApiService.activateUser(id);

        if (!activeUser.success) {
            this.setState({ isLoading: false });
            toastService.error(activeUser.message);
            return;
        }

        const userProfile = await userApiService.get(this.state.userId);

        if (!userProfile.success) {
            this.setState({ isLoading: false });
            toastService.error(userProfile.message);
            return;
        }

        this.setState({ user: userProfile.data, isLoading: false });

        toastService.success("Account Activated.", "Action Completed");
    };

    blockUserAccount = async (id: number) => {
        this.setState({ isLoading: true });

        const result = await userApiService.blockUser(id);

        this.setState({ isLoading: false });
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        //history.push("/users");
    };

    resetKycStatus = async (id: number) => {
        this.setState({ isLoading: true });

        const result = await kycSubmissionApiService.resetKycStatus(id);

        this.setState({ isLoading: false });
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        window.location.reload();
    };

    resetEmail = async (id: number) => {
        this.setState({ isLoading: true });

        const result = await userApiService.resetEmail(id);

        this.setState({ isLoading: false });
        if (!result.success) {
            toastService.error(result.message);
            return;
        }
        window.location.reload();
    };

    resetMobile = async (id: number) => {
        this.setState({ isLoading: true });

        const result = await userApiService.resetPhone(id);

        this.setState({ isLoading: false });
        if (!result.success) {
            toastService.error(result.message);
            return;
        }

        window.location.reload();
    };

    onImageSelected = async (image: File) => {
        if (!utils.isValidImage(image)) {
            toastService.error("Format not supported! Only Image files are allowed.", "File Format");
            return false;
        }

        if (image.size > Image_File_Size) {
            toastService.error("Files less than 5MB are supported only.", "File Size");
            return false;
        }

        this.setState({ photoUploading: true });
        const compressedImage = await imageCompression(image, { maxSizeMB: Image_File_Size, useWebWorker: true });
        const base64Image = await utils.convertFileToBase64(compressedImage);

        const request: UpdateUserPhotoRequest = {
            photo: base64Image as string,
            userId: this.state.userId,
            userType: UserTypeRelation.Individual,
        };

        const uploadResult = await userApiService.updateUserPhoto(request);

        if (!uploadResult.success) {
            this.setState({ photoUploading: false });
            toastService.error("Could not upload the image. Please try again.", "Upload Failed");
            return;
        }

        // Update user details with the uploaded image ID
        this.setState((prevState) => ({
            user: {
                ...prevState.user,
                photoStorageId: uploadResult.data,
                photo: base64Image as string,
            } as User,
            photoUploading: false,
        }));

        toastService.success("Profile image updated successfully.");
    };

    fileInput = (input: boolean) => {
        if (!input) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            return;
        }
    };
    getImageSource = (): string => {
        return this.state.user?.photo === null ? "" : utils.appendBase64Data(this.state.user!.photo);
    };

    onFieldUpdate = (fieldName: string, value: any) => {
        const userData = this.state.user as any;
        userData[fieldName] = value;
        this.setState({ user: userData });
    };

    downloadDocuments = async (submissionId: number) => {
        this.setState({ isDocumentLoading: true });
        const documentsResult = await kycSubmissionApiService.getDocuments(submissionId);
        if (!documentsResult.success) {
            this.setState({ isDocumentLoading: false });
            toastService.error(documentsResult.message);
            return;
        }
        const zip = new JSZip();
        const photoZip = zip.folder("documents");
        const data = documentsResult.data;
        for (let i = 0; i < data.length; i++) {
            const result = data[i];
            await this.onDownloadFromFileStorage(result.fileStorageId, this.getReadableFileName(result), photoZip);
        }
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "documents");
        this.setState({ isDocumentLoading: false });
    };

    getReadableFileName = (doc: KycSubmissionDocument): string => {
        const extension = doc.fileStorageId.split('.').pop();
        const label = doc.fileName ?? "Document";
        return `${label}.${extension}`;
    };

    onDownloadFromFileStorage = async (fileStorageId: string, secondaryLabel: string, photoZip: any): Promise<void> => {
        const imagePath = await fileService.GetFile(fileStorageId);

        if (!imagePath.success) {
            this.setState({ isLoading: false });
            toastService.error(imagePath.message);
            return;
        }

        photoZip.file(secondaryLabel, imagePath.data, { base64: true });

        this.setState({ isLoading: false });
    };
    showDeleteConfirmModal = (action: DeleteAction) => {
        this.setState({
            isDeleteConfirmModalVisible: true,
            deleteAction: action,
        });
    };

    hideDeleteModal = () => {
        this.setState({
            isDeleteConfirmModalVisible: false,
            deleteAction: null,
        });
    };


    deleteConfirm = async (reason: string) => {
        if (!this.state.user?.userId || !this.state.deleteAction) return;

        try {
            let response;

            switch (this.state.deleteAction) {
                case "HARD_DELETE":
                    response = await authUsersApiService.hardDeleteUser({
                        userId: this.state.user.userId,
                        reason,
                    });
                    break;

                case "SOFT_DELETE":
                    response = await authUsersApiService.softDeleteUser({
                        userId: this.state.user.userId,
                        reason,
                    });
                    break;

                case "RESTORE":
                    response = await authUsersApiService.restoreUser({
                        userId: this.state.user.userId,
                        reason,
                    });
                    break;
            }

            if (response?.success) {
                let successMessage = "";

                if (this.state.deleteAction === "HARD_DELETE") {
                    successMessage = "User permanently deleted.";
                } else if (this.state.deleteAction === "SOFT_DELETE") {
                    successMessage = "User archived successfully.";
                } else {
                    successMessage = "User restored successfully.";
                }



                this.setState({
                    isDeleteConfirmModalVisible: false,
                    deleteAction: null,
                });


                toastService?.success(

                    successMessage,

                );
                setTimeout(() => {
                    history.push("/users")
                }, 1000);
            } else {
                toastService.error(response?.message || "Operation failed");
            }
        } catch (error) {
            console.error(error);
            toastService.error("Unexpected error occurred.");
        }
    };

    getCustomerOutgoingFreezeRequest = (reasonText?: string): CustomerOutgoingFreezeRequest => {
        const user = this.state.user;

        return {
            sourceEntityType: EntityType.Individual,
            sourceEntityId: user?.id ?? this.state.userId,
            tenantId: user?.merchantId ?? 0,
            reasonText,
        };
    };

    getOutgoingFreezeReason = (defaultReason: string): string => {
        const reason = this.state.outgoingFreezeReason.trim();
        return reason.length > 0 ? reason : defaultReason;
    };

    formatFreezeDateTime = (date?: string): string => {
        if (!date) {
            return "N/A";
        }

        return utils.formatDateTimeLong(date);
    };

    loadOutgoingFreezeStatus = async () => {
        if (!this.state.user) {
            return;
        }

        this.setState({ outgoingFreezeLoading: true });
        const result = await orchestratorApiService.getCustomerOutgoingFreeze(this.getCustomerOutgoingFreezeRequest());

        if (!result.success) {
            this.setState({ outgoingFreezeLoading: false });
            toastService.error(result.message || "Could not load outgoing freeze status.");
            return;
        }

        this.setState({
            outgoingFreeze: result.data,
            outgoingFreezeLoading: false,
        });
    };

    activateOutgoingFreeze = () => {
        confirmAction(
            "Freeze this customer's outgoing payments? New outgoing payment attempts will be rejected until Compliance releases the freeze.",
            "Freeze Outgoing Payments",
            async () => {
                this.setState({ outgoingFreezeActionLoading: true });
                const result = await orchestratorApiService.activateCustomerOutgoingFreeze(
                    this.getCustomerOutgoingFreezeRequest(this.getOutgoingFreezeReason("Manual outgoing freeze from compliance user profile.")),
                );

                if (!result.success) {
                    this.setState({ outgoingFreezeActionLoading: false });
                    toastService.error(result.message || "Could not activate outgoing freeze.");
                    return;
                }

                this.setState({
                    outgoingFreeze: result.data,
                    outgoingFreezeActionLoading: false,
                    outgoingFreezeReason: "",
                });
                toastService.success("Customer outgoing freeze activated.", "Action Completed");
            },
        );
    };

    releaseOutgoingFreeze = () => {
        confirmAction(
            "Release this customer's outgoing freeze? New outgoing payment attempts will no longer be rejected by this freeze.",
            "Release Outgoing Freeze",
            async () => {
                this.setState({ outgoingFreezeActionLoading: true });
                const result = await orchestratorApiService.releaseCustomerOutgoingFreezeForCustomer(
                    this.getCustomerOutgoingFreezeRequest(this.getOutgoingFreezeReason("Manual outgoing freeze release from compliance user profile.")),
                );

                if (!result.success) {
                    this.setState({ outgoingFreezeActionLoading: false });
                    toastService.error(result.message || "Could not release outgoing freeze.");
                    return;
                }

                this.setState({
                    outgoingFreeze: result.data,
                    outgoingFreezeActionLoading: false,
                    outgoingFreezeReason: "",
                });
                toastService.success("Customer outgoing freeze released.", "Action Completed");
            },
        );
    };

    renderOutgoingFreezePanel = () => {
        const freeze = this.state.outgoingFreeze;
        const isActive = freeze?.isActive === true;
        const canChangeFreeze = isKYCApproveAllowed() || isSuperAdmin();

        return (
            <AppCard>
                <AppCard.Header title="Outgoing Payments Freeze" />
                <AppCard.Body>
                    {this.state.outgoingFreezeLoading ? (
                        <LoadingDots />
                    ) : (
                        <div className="space-y-4">
                            <Row>
                                <Col md={3}>
                                    <Label>
                                        <b>Current Status</b>
                                    </Label>
                                </Col>
                                <Col md={9}>
                                    <AppBadge variant={isActive ? "danger" : "success"}>
                                        {isActive ? "Frozen" : "Not Frozen"}
                                    </AppBadge>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Label>
                                        <b>Reason</b>
                                    </Label>
                                </Col>
                                <Col md={9}>
                                    <span>{freeze?.reasonText || freeze?.reasonCode || "No active freeze."}</span>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Label>
                                        <b>Audit</b>
                                    </Label>
                                </Col>
                                <Col md={9}>
                                    {isActive ? (
                                        <span>
                                            Activated by {freeze?.activatedBy || "N/A"} at {this.formatFreezeDateTime(freeze?.activatedAt)}
                                            {freeze?.relatedTransactionId ? `, related transfer ${freeze.relatedTransactionId}` : ""}
                                        </span>
                                    ) : (
                                        <span>
                                            Released by {freeze?.releasedBy || "N/A"} at {this.formatFreezeDateTime(freeze?.releasedAt)}
                                            {freeze?.releaseReason ? `, reason: ${freeze.releaseReason}` : ""}
                                        </span>
                                    )}
                                </Col>
                            </Row>
                            {canChangeFreeze && (
                                <>
                                    <Row>
                                        <Col md={3}>
                                            <Label for="outgoingFreezeReason">
                                                <b>Operator Note</b>
                                            </Label>
                                        </Col>
                                        <Col md={9}>
                                            <InputTextarea
                                                id="outgoingFreezeReason"
                                                className="w-full"
                                                rows={3}
                                                value={this.state.outgoingFreezeReason}
                                                placeholder="Optional reason stored with the freeze or release action"
                                                onChange={(e) => this.setState({ outgoingFreezeReason: e.target.value })}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={{ size: 9, offset: 3 }} className="flex gap-2">
                                            <Button
                                                label={isActive ? "Release Freeze" : "Freeze Outgoing Payments"}
                                                severity={isActive ? "success" : "danger"}
                                                disabled={this.state.outgoingFreezeActionLoading}
                                                loading={this.state.outgoingFreezeActionLoading}
                                                onClick={isActive ? this.releaseOutgoingFreeze : this.activateOutgoingFreeze}
                                            />
                                            <Button
                                                label="Refresh"
                                                severity="secondary"
                                                outlined
                                                disabled={this.state.outgoingFreezeActionLoading}
                                                onClick={this.loadOutgoingFreezeStatus}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            )}
                            {!canChangeFreeze && (
                                <Row>
                                    <Col md={{ size: 9, offset: 3 }}>
                                        <span className="text-muted">Current user can view the status but cannot change outgoing freezes.</span>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    )}
                </AppCard.Body>
            </AppCard>
        );
    };

    renderUserDetails = () => {
        const isReadOnly = this.state.user?.kycStatus != null && this.kycStatusTypes.get(this.state.user?.kycStatus ?? -1) !== "New" ? true : false;

        if (this.state.isLoading || !this.state.user) {
            return (
                <Row>
                    <Col md={12} lg={12}>
                        <AppCard className="min-h-[500px] align-center">
                            <LoadingDots />
                        </AppCard>
                    </Col>
                </Row>
            );
        }


        return (
            <Row>
                <Col md="3" lg="3" sm="12" className="p-8">
                    <ProfileImage
                        imageStyle={{ width: "100%" }}
                        className="rounded-circle"
                        src={this.getImageSource()}
                        alt=""
                        onImageSelected={this.onImageSelected}
                        fileInput={this.fileInput}
                        photoUploading={this.state.photoUploading}
                    />
                </Col>
                <Col md={9} lg={9} sm="12">
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="firstName">First Name</Label>
                                <InputText
                                    value={this.state.user.firstName}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    id="firstName"
                                    onChange={(e) => this.onFieldUpdate("firstName", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="middleName">Middle Name</Label>
                                <InputText
                                    value={this.state.user.middleName}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    id="middleName"
                                    onChange={(e) => this.onFieldUpdate("middleName", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="lastName">Last Name</Label>
                                <InputText
                                    value={this.state.user.lastName}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    id="lastName"
                                    onChange={(e) => this.onFieldUpdate("lastName", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="nonLatinFirstName">Non-Latin First Name</Label>
                                <InputText
                                    value={this.state.user.nonLatinFirstName}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    id="nonLatinFirstName"
                                    onChange={(e) => this.onFieldUpdate("nonLatinFirstName", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="nonLatinMiddleName">Non-Latin Middle Name</Label>
                                <InputText
                                    value={this.state.user.nonLatinMiddleName}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    id="nonLatinMiddleName"
                                    onChange={(e) => this.onFieldUpdate("nonLatinMiddleName", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="nonLatinLastName">Non-Latin Last Name</Label>
                                <InputText
                                    value={this.state.user.nonLatinLastName}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    id="nonLatinLastName"
                                    onChange={(e) => this.onFieldUpdate("nonLatinLastName", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="personalIdentificationNumber">Personal Identification Number</Label>
                                <InputText
                                    value={this.state.user.personalIdentificationNumber}
                                    className={"form-control"}
                                    type="text"
                                    name="personalIdentificationNumber"
                                    id="personalIdentificationNumber"
                                    onChange={(e) => this.onFieldUpdate("personalIdentificationNumber", e.target.value)}
                                    readOnly={isReadOnly}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="email">Email Address</Label>
                                <InputText
                                    value={this.state.user.email}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    type="email"
                                    name="email"
                                    id="email"
                                    onChange={(e) => this.onFieldUpdate("email", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3} className="flex items-center pt-4 px-2.5">
                            <AppBadge variant={this.getVerificationClassName(this.state.user.isEmailVerified) as any}>
                                {this.state.user.isEmailVerified ? "Verified" : "Not Verified"}
                            </AppBadge>
                        </Col>

                        {isUserProfileUpdateAllowed() && (
                            <Col md={3} className="pt-8 px-2.5">
                                <a href="#" onClick={this.resetEmailDialog}>
                                    Reset Email Address
                                </a>
                                <br></br>
                                <a href="#" onClick={this.resetMobileDialog}>
                                    Reset Mobile
                                </a>
                                <br></br>
                                {this.kycStatusTypes.get(this.state.user.kycStatus ?? 1) !== "New" && (
                                    <a href="#" onClick={this.resetKycStatusDialog}>
                                        Reset Kyc Status
                                    </a>
                                )}
                                <br></br>
                                {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.user.status) === "Deactivated" && (
                                    <>
                                        <a href="#" onClick={() => this.deactiveOrActiveUserAccount(this.state.userId)}>
                                            Activate
                                        </a>
                                        <span> | </span>
                                        <a style={{ padding: "0 2px" }} href="#" onClick={() => this.closeUser(this.state.userId)}>
                                            Close
                                        </a>
                                        <span> | </span>
                                        <a style={{ padding: "0 2px" }} href="#" onClick={() => this.blockUserAccount(this.state.userId)}>
                                            Add to Blacklist
                                        </a>
                                    </>
                                )}

                                {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.user.status) === "Active" && (
                                    <>
                                        <a href="#" onClick={this.deactivateDialog}>
                                            Deactivate
                                        </a>
                                    </>
                                )}
                                <br></br>
                                 {isDataManager() && !this.state.user?.cbsId && (    <>
                                        <a href="#" onClick={()=>this.showDeleteConfirmModal("HARD_DELETE")}>
                                            Delete User Permanently
                                        </a>
                                    </>
                                )}
                                <br></br>
                                {canSoftDeleteUser() && (
                                    <>
                                        {!utils.isSoftDeleted(this.state.user?.email || "") ? (
                                            <a href="#" onClick={() => this.showDeleteConfirmModal("SOFT_DELETE")}>
                                                Archive User
                                            </a>
                                        ) : (
                                            <a href="#" onClick={() => this.showDeleteConfirmModal("RESTORE")}>
                                                Restore User
                                            </a>
                                        )}
                                    </>
                                )}
                            </Col>
                        )}
                    </Row>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="mobile">Mobile</Label>
                                <InputText
                                    value={this.state.user.phoneNumber}
                                    readOnly={isReadOnly}
                                    className={"form-control"}
                                    type="text"
                                    name="phoneNumber"
                                    id="phoneNumber"
                                    onChange={(e) => this.onFieldUpdate("phoneNumber", e.target.value)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3} className="flex items-center pt-4 px-2.5">
                            <AppBadge variant={this.getVerificationClassName(this.state.user.isPhoneNumberVerified) as any}>
                                {this.state.user.isPhoneNumberVerified === true ? "Verified" : "Not Verified"}
                            </AppBadge>
                        </Col>
                        {isUserProfileUpdateAllowed() && <Col md={3} className="pt-8 px-2.5"></Col>}
                    </Row>


                    <Row form>
                        <Col md={3}>
                            <FormGroup>
                                <Label>
                                    <b>Kyc Status</b>
                                </Label>
                            </FormGroup>
                        </Col>
                        <Col className="pl-0" md={3}>
                            <AppBadge variant={this.getKycStatusVerificationClassName(this.state.user.kycStatus ?? 1) as any}>
                                {this.kycStatusTypes.get(this.state.user.kycStatus ?? 1)}
                            </AppBadge>
                        </Col>
                    </Row>
                    <Row form>
                        <Col md={3}>
                            <Label>
                                <b>CBS Onboarded</b>
                            </Label>
                        </Col>
                        <Col className="pl-0" md={3}>
                            <AppBadge variant={this.getCbsOnboardedClassName(this.state.user.cbsOnboarded) as any}>
                                {this.state.user.cbsOnboarded ? "Onboarded" : "Not-onboared"}
                            </AppBadge>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label>
                                    <b>User Status</b>
                                </Label>
                            </FormGroup>
                        </Col>
                        <Col className="pl-0" md={3}>
                            <AppBadge variant={this.getStatusVerificationClassName(this.state.user.status) as any}>
                                {this.statusTypes.get(this.state.user.status)}
                            </AppBadge>
                        </Col>
                        {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.user.status) === "Deactivated" && (
                            <Col>
                                <a href="#" onClick={() => this.deactiveOrActiveUserAccount(this.state.userId)}>
                                    Activate
                                </a>
                                <span> | </span>
                                <a style={{ padding: "0 2px" }} href="#" onClick={() => this.closeUser(this.state.userId)}>
                                    Close
                                </a>
                                <span> | </span>
                                <a style={{ padding: "0 2px" }} href="#" onClick={() => this.blockUserAccount(this.state.userId)}>
                                    Add to Blacklist
                                </a>
                            </Col>
                        )}
                        {isUserProfileUpdateAllowed() && this.statusTypes.get(this.state.user.status) === "Active" && <Col></Col>}
                    </Row>

                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label>
                                    <b>KYC Documents</b>
                                </Label>
                            </FormGroup>
                        </Col>
                        <Col className="pl-0">
                            <Button
                                className="badge align-top badge-success d-flex align-items-center"
                                iconPos="right"
                                label="Download"
                                type="button"
                                severity="success"
                                icon={this.state.isDocumentLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                                disabled={this.state.isDocumentLoading}
                                onClick={() => this.downloadDocuments(this.state.user?.kycSubmissionId ?? 0)}
                            />
                        </Col>
                    </Row>

                    <Row form>
                        <Col md={3}>
                            <Label>
                                <b>Crypto Status</b>
                            </Label>
                        </Col>
                        <Col className="pl-0" md={3}>
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    checked={this.state.user.cryptoCustomer}
                                    onChange={(e) => this.handleCryptoStatusChange(e)}
                                    disabled={isReadOnly}
                                />
                                <span className={classNames(this.getCryptoCustomerClassName(this.state.user.cryptoCustomer), "font-semibold text-sm")}>
                                    {this.state.user.cryptoCustomer ? "Crypto customer" : "Non-Crypto customer"}
                                </span>
                            </div>
                        </Col>
                    </Row>
                    <Row form>
                        <Col md={3}>
                            <Label>
                                <b>Primary Language</b>
                            </Label>
                        </Col>
                        <Col className="pl-0" md={3}>
                            <Label>{this.state.user.primaryLanguage}</Label>
                        </Col>
                    </Row>
                    <Row form>
                        <Col md={3}>
                            <Label>
                                <b>Merchant</b>
                            </Label>
                        </Col>
                        <Col className="pl-0" md={3}>
                            {this.state.user.merchant}&emsp;&emsp;<b className="text-danger">Branch - {this.state.user.flexCubeBranch}</b>
                        </Col>
                    </Row>
                    <Row form>
                        <Col md={6}>
                            <InlineEditableInput
                                inputType={InputType.Text}
                                readOnly={true}
                                title="Tariff"
                                value={this.state.user.tariff}
                                onSubmit={(value) => {
                                    this.updateValue("tariff", value);
                                }}
                                className="pl-0"
                            />
                        </Col>
                    </Row>
                    <Row form>
                        <Col md={6}>
                            {this.state.user.kycStatus != null && (
                                <InlineEditableInput
                                    inputType={InputType.DropDown}
                                    readOnly={true}
                                    title="Kyc Level"
                                    options={this.kycLevels}
                                    value={this.getKycLevel(this.state.user.kycLevel)!}
                                    onSubmit={(value) => {
                                        this.onKycLevelSubmit(parseInt(value));
                                    }}
                                />
                            )}
                        </Col>
                    </Row>
                </Col>
            </Row>
        );
    };

    render() {

        return (
            <>
                <DeleteUserDialog
                    visible={this.state.isDeleteConfirmModalVisible}
                    onHide={this.hideDeleteModal}
                    action={this.state.deleteAction}
                    onConfirm={(reason) => {
                        return this.deleteConfirm(reason);
                    }}
                />
                <PageLayout heading="User Profile" icon={UserCog}>
                    <div className="space-y-4">
                        <AppCard>
                            <AppCard.Header title="User Details" />
                            <AppCard.Body>{this.renderUserDetails()}</AppCard.Body>
                        </AppCard>
                        {this.state.user && this.renderOutgoingFreezePanel()}
                        {this.state.user && (
                            <MerchantWalletsComponent
                                Id={this.state.userId}
                                cbsId={this.state.user.cbsId}
                                walletType={WalletType.User}
                                individualStatus={this.state.user.status}
                            />
                        )}
                        <AppCard>
                            <AppCard.Body className="!p-0">
                                <TabView
                                    activeIndex={this.state.activeTabIndex}
                                    onTabChange={(e) => {
                                        this.setState({ activeTabIndex: e.index });
                                    }}
                                >
                                    <TabPanel
                                        pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                        headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                        header="General"
                                    >
                                        <GeneralDataComponent
                                            userData={this.state.user}
                                            groupsSelectItem={this.state.groupsSelectItem}
                                            kycStatusTypes={this.kycStatusTypes}
                                        />
                                    </TabPanel>
                                    {(isSuperAdmin() || isComplianceOfficer() || isComplianceOfficerApprover()) && (
                                        <TabPanel
                                            pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                            headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                            header="KYC"
                                        >
                                            {this.state.user && (
                                                <>
                                                    <AdditionalCustomerDocumentsPanel
                                                        individualId={this.state.user.id}
                                                        isActive={this.state.user.status === IndividualStatus.Active}
                                                    />
                                                    <UserKycSubmissionsComponent
                                                        individualId={this.state.userId}
                                                        cbsId={this.state.user.cbsId}
                                                        kycStatusTypes={this.kycStatusTypes}
                                                        userKycStatusClassesMap={this.userKycStatusClassesMap}
                                                        kycStatusValue={this.state.user.kycStatus}
                                                    />
                                                </>
                                            )}
                                        </TabPanel>
                                    )}
                                    {isIndividualDocumentsViewAllowed() && this.state.documentsFeatureEnabled && (
                                        <TabPanel
                                            pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                            headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                            header="Documents"
                                        >
                                            {this.state.user && (
                                                <UserDocumentsComponent
                                                    individualId={this.state.userId}
                                                    currentKycSubmissionId={this.state.user.kycSubmissionId}
                                                />
                                            )}
                                        </TabPanel>
                                    )}
                                    <TabPanel
                                        pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                        headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                        header="Financial"
                                    >
                                        <FinancialDataComponent
                                            userId={this.state.userId}
                                            cbsOnboarded={this.state.user?.cbsOnboarded}
                                        />
                                    </TabPanel>
                                    {isIndividualTransactionViewAllowed() && (
                                        <TabPanel
                                            pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                            headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                            header="Transactions"
                                        >
                                            <UserTransactions
                                                userEmail={this.state.user?.email}
                                                userId={this.state.userId}
                                                showExpander={false}
                                            />
                                        </TabPanel>
                                    )}
                                    {isJournalingAllowed() && (
                                        <TabPanel
                                            pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                            headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                            header="Journaling"
                                        >
                                            <UserJournalingComponent userId={this.state.userId} />
                                        </TabPanel>
                                    )}
                                    {(isSuperAdmin()) && this.state.user && (
                                        <TabPanel
                                            pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                            headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                            header="Sanctions"
                                        >
                                            <NilWhitelistComponent cbsId={this.state.user.cbsId} />
                                        </TabPanel>
                                    )}
                                    <TabPanel
                                        pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                        headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                        header="Devices"
                                    >
                                        <UserDevicesComponent userId={this.state.userId} />
                                    </TabPanel>
                                    <TabPanel
                                        pt={{ content: { className: "pt-0 px-5 pb-5" } }}
                                        headerTemplate={createTabHeaderTemplate(this.state.activeTabIndex)}
                                        header="Comments"
                                    >
                                        <UserCommentsComponent />
                                    </TabPanel>
                                </TabView>
                            </AppCard.Body>
                        </AppCard>
                    </div>
                </PageLayout>
            </>
        );
    }
}
